/* eslint-disable no-case-declarations */

import { type EEntityType, EWebsocketTopics } from '@sharedInterfaces/globalEnums';
import type { TAttachmentUploadResponse } from '@sharedInterfaces/IAttachment';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { WebSocketClient } from '@src/APIs/WebSockets/WebSocketClient';
import { useConfirmDeleteDialog } from '@src/App/Components/Dialogs/ConfirmDelete/confirmDelete';
import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import Attachment from '@src/Objects/Attachment';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { uploadFile } from '../../Dialogs/AnalyseSkillsDialog/AnalyseCvDialog';

export interface UseAttachmentUploadProps {
    entityId: number;
    entityType: EEntityType;
    onUploadFinished?: (attachment: Attachment) => void;
    onFileContent?: (attachment: Attachment, fileContent: string) => void;
}

export interface AttachmentUploadState {
    uploading: boolean;
    currentFile: File | null;
    uploadedAttachments: Attachment[];
}

export const useAttachmentUpload = ({
    entityId,
    entityType,
    onUploadFinished,
    onFileContent,
}: UseAttachmentUploadProps) => {
    const showErrorDialog = useErrorDialog();
    const confirmDelete = useConfirmDeleteDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).attachments;

    const [state, setState] = useState<AttachmentUploadState>({
        uploading: false,
        currentFile: null,
        uploadedAttachments: [],
    });

    const topic = EWebsocketTopics.attachmentUpload;

    useEffect(() => {
        const subKey = WebSocketClient.subscribe<TAttachmentUploadResponse>(
            topic,
            async (data: TAttachmentUploadResponse) => {
                switch (data.type) {
                    case 'success': {
                        if (!data.attachment) return showErrorDialog(new Error('No data returned'));
                        const attachment = new Attachment(data.attachment);
                        setState((prev) => ({
                            ...prev,
                            uploading: false,
                            currentFile: null,
                            uploadedAttachments: [
                                ...prev.uploadedAttachments.filter((a) => a.id !== attachment.id),
                                attachment,
                            ],
                        }));
                        onUploadFinished?.(attachment);
                        break;
                    }

                    case 'fileContent':
                        if (!data.attachment) return showErrorDialog(new Error('No data returned'));
                        onFileContent?.(new Attachment(data.attachment), data.fileContent);
                        break;

                    case 'url':
                        if (!state.currentFile) {
                            setState((prev) => ({ ...prev, uploading: false }));
                            return showErrorDialog(new Error('File not found'));
                        }
                        await uploadFile(data, state.currentFile).catch((ex) => {
                            showErrorDialog(ex, 'Error uploading file');
                            setState((prev) => ({ ...prev, uploading: false, currentFile: null }));
                        });
                        break;

                    case 'error':
                        if (data.error === 'File already exists') {
                            confirmDelete(
                                state.currentFile?.name || '',
                                langStrings.confirmOverride,
                                langStrings.oftenUsed.override,
                                langStrings.confirmOverrideTitle,
                            ).then((confirm) => {
                                if (confirm && state.currentFile) {
                                    startUpload(state.currentFile, true);
                                } else {
                                    setState((prev) => ({ ...prev, uploading: false, currentFile: null }));
                                }
                            });
                        } else {
                            showErrorDialog(new Error(data.error));
                            setState((prev) => ({ ...prev, uploading: false, currentFile: null }));
                        }
                        break;
                }
            },
        );

        return () => WebSocketClient.unsubscribe(subKey);
    }, [state.currentFile, entityId, entityType, onUploadFinished, onFileContent]);

    const startUpload = (file: File, override = false) => {
        setState((prev) => ({ ...prev, uploading: true, currentFile: file }));
        WebSocketClient.sendMessage(topic, { entityId, entityType, fileName: file.name, override });
    };

    const removeAttachment = (attachmentId: string) => {
        setState((prev) => ({
            ...prev,
            uploadedAttachments: prev.uploadedAttachments.filter((a) => a.id !== attachmentId),
        }));
    };

    return {
        ...state,
        startUpload,
        removeAttachment,
    };
};
